import { CURVE_STROKE, type Curve } from '@bcecloud/portal-ui';
import { getJson, sendJson } from '@/lib/api';

/**
 * Analytics → Pricing: each program sold this year priced at 10 to 300 people
 * under every Retail price list since 2015, the 2025 list and the 2026
 * calculator, against actual cost. Mirrors the API's PricingAnalytics
 * (apps/api/src/api/v3/pricing-analytics/pricing-curves.ts).
 */

export const PRICING_LINE = {
  retail2015: 'retail2015',
  retail2021: 'retail2021',
  retail2023: 'retail2023',
  retail2024: 'retail2024',
  nonLocal2025: 'nonLocal2025',
  calc2026: 'calc2026',
  calc2026Fixed: 'calc2026Fixed',
  fullCost: 'fullCost',
  actualCost: 'actualCost',
} as const;
export type PricingLineKey = (typeof PRICING_LINE)[keyof typeof PRICING_LINE];

export const PROGRAM_KIND = { inPerson: 'in-person', virtual: 'virtual' } as const;
export type ProgramKind = (typeof PROGRAM_KIND)[keyof typeof PROGRAM_KIND];

export const PRICING_VIEW = { price: 'price', margin: 'margin' } as const;
export type PricingView = (typeof PRICING_VIEW)[keyof typeof PRICING_VIEW];

export const FLOOR_SOURCE = { salesforce: 'salesforce', assumed: 'assumed' } as const;
export type FloorSource = (typeof FLOOR_SOURCE)[keyof typeof FLOOR_SOURCE];

export type PricingSeverity = 'high' | 'medium' | 'info';

export interface PricingFlag {
  readonly key: string;
  readonly severity: PricingSeverity;
  readonly text: string;
}

export interface PricingCostLine {
  readonly name: string;
  readonly amount: number;
  readonly basis: string;
  readonly type: string;
  readonly teamsPerCost: number | null;
}

export interface PricingProgram {
  readonly id: string;
  readonly name: string;
  readonly kind: ProgramKind;
  readonly signedThisYear: number;
  readonly marginSetting: number | null;
  readonly teamSizeText: string;
  readonly teamSizeUsed: number;
  readonly nonLocal2025: { readonly base: number; readonly rate: number | null; readonly included: number | null } | null;
  readonly base2026: number | null;
  readonly lines: Readonly<Partial<Record<PricingLineKey, ReadonlyArray<number | null>>>>;
  readonly at100: {
    readonly retail2024: number | null;
    readonly nonLocal2025: number | null;
    readonly calc2026: number | null;
    readonly calc2026Fixed: number | null;
    readonly fullCost: number | null;
    readonly actualCost: number | null;
    readonly gap2026Vs2025: number | null;
    readonly kept2025: number | null;
    readonly kept2026: number | null;
    readonly kept2026Fixed: number | null;
  };
  readonly costLines: readonly PricingCostLine[];
  readonly flags: readonly PricingFlag[];
}

export interface PricingAnalytics {
  readonly headcounts: readonly number[];
  readonly probe: number;
  readonly floorSource: FloorSource;
  readonly soldSince: string;
  readonly syncedAt: string | null;
  readonly programs: readonly PricingProgram[];
}

export const PRICING_ANALYTICS_QUERY_KEY = ['pricing-analytics'] as const;

export function readPricingAnalytics(signal?: AbortSignal): Promise<PricingAnalytics> {
  return getJson<PricingAnalytics>('/analytics/pricing', {}, signal);
}

/** Re-reads the pricing mirrors from Salesforce now. */
export function syncPricingAnalytics(): Promise<unknown> {
  return sendJson('post', '/analytics/pricing/sync');
}

// ─── Lines: names, colors, which cost each price is measured against ─────────

/**
 * Colors validated with the dataviz checks (adjacent pairs CVD-safe in light
 * and dark): Retail years share one blue, fading with age; 2025 green;
 * 2026 today magenta; 2026 fixed yellow; costs neutral.
 */
const BLUE = '#2a78d6';
const GREEN = '#008300';
const MAGENTA = '#e87ba4';
const YELLOW = '#eda100';
const NEUTRAL = '#667085';

export interface PricingLineSpec {
  readonly key: PricingLineKey;
  readonly label: string;
  readonly detail: string;
  readonly color: string;
  readonly opacity?: number;
  readonly stroke?: Curve['stroke'];
  /** Cost lines are hidden in the margin view: they are its zero. */
  readonly isCost: boolean;
}

export const PRICING_LINES: readonly PricingLineSpec[] = [
  { key: PRICING_LINE.retail2015, label: 'Retail 2015 to 2021', detail: 'The undated Retail book.', color: BLUE, opacity: 0.28, isCost: false },
  { key: PRICING_LINE.retail2021, label: 'Retail 2021', detail: 'Set prices to 50 people, then a per-person rate that falls with size.', color: BLUE, opacity: 0.42, isCost: false },
  { key: PRICING_LINE.retail2023, label: 'Retail 2023', detail: 'Same shape.', color: BLUE, opacity: 0.58, isCost: false },
  { key: PRICING_LINE.retail2024, label: 'Retail 2024', detail: 'Same shape; retired in 2025.', color: BLUE, opacity: 0.76, isCost: false },
  { key: PRICING_LINE.nonLocal2025, label: '2025 Non Local', detail: 'A set price up to about 30 people, then one rate per extra person.', color: GREEN, isCost: false },
  { key: PRICING_LINE.calc2026, label: '2026 today', detail: 'What Salesforce calculates now: fees counted as cost, then marked up (75% margin = cost × 1.75).', color: MAGENTA, isCost: false },
  { key: PRICING_LINE.calc2026Fixed, label: '2026 fixed', detail: 'Actual cost ÷ (1 − margin): a 75% margin keeps exactly 75% of the price.', color: YELLOW, isCost: false },
  { key: PRICING_LINE.actualCost, label: 'Actual cost', detail: 'Every cost line except the per-person fees. Margins are measured against this.', color: NEUTRAL, stroke: CURVE_STROKE.dotted, isCost: true },
  { key: PRICING_LINE.fullCost, label: 'Cost with fees', detail: 'Also counts Service Fee, Workshop and Player Fee.', color: NEUTRAL, stroke: CURVE_STROKE.dashed, isCost: true },
];

export const DEFAULT_PRICING_LINES: readonly PricingLineKey[] = [
  PRICING_LINE.retail2015,
  PRICING_LINE.retail2021,
  PRICING_LINE.retail2023,
  PRICING_LINE.retail2024,
  PRICING_LINE.nonLocal2025,
  PRICING_LINE.calc2026,
  PRICING_LINE.calc2026Fixed,
  PRICING_LINE.actualCost,
];

const isLineKey = (value: string): value is PricingLineKey => (Object.values(PRICING_LINE) as string[]).includes(value);

/** The ticked lines from the URL (comma-separated), or the defaults. */
export function parseLines(param: string | null): ReadonlySet<PricingLineKey> {
  if (param === null) return new Set(DEFAULT_PRICING_LINES);
  return new Set(param.split(',').filter(isLineKey));
}

/** Share of a price left after actual cost; null where either is missing. A loss floors at −100%. */
export function keptShare(price: number | null | undefined, cost: number | null | undefined): number | null {
  if (price === null || price === undefined || price <= 0 || cost === null || cost === undefined) return null;
  return Math.max(-1, (price - cost) / price);
}

/**
 * The curves to draw for one program: the ticked lines that have data, as
 * prices or, in the margin view, as the share kept after actual cost.
 *
 * @param program - The program.
 * @param lines - Ticked lines.
 * @param view - Price or margin.
 * @returns Curves in legend order.
 */
export function curvesFor(program: PricingProgram, lines: ReadonlySet<PricingLineKey>, view: PricingView): Curve[] {
  const cost = program.lines.actualCost ?? [];
  return PRICING_LINES.filter((spec) => lines.has(spec.key) && program.lines[spec.key] !== undefined)
    .filter((spec) => view === PRICING_VIEW.price || !spec.isCost)
    .map((spec) => {
      const values = program.lines[spec.key] ?? [];
      return {
        key: spec.key,
        label: spec.label,
        color: spec.color,
        ...(spec.opacity !== undefined ? { opacity: spec.opacity } : {}),
        ...(spec.stroke !== undefined ? { stroke: spec.stroke } : {}),
        values: view === PRICING_VIEW.price ? values : values.map((v, i) => keptShare(v, cost[i])),
      };
    });
}

const usdFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const pctFormat = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 });
const signedPctFormat = new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0, signDisplay: 'exceptZero' });

export const formatUsd = (value: number): string => usdFormat.format(value);
export const formatPct = (value: number): string => pctFormat.format(value);
export const formatSignedPct = (value: number): string => signedPctFormat.format(value);
